import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import API from "../../api";
import toastr from "toastr";
import { Expense } from "../../pages/Expenses"; // re-use the shared interface

interface ExpenseModalProps {
  show: boolean;
  onClose: () => void;
  mode: "add" | "edit";
  expenseData: Expense | null;
  onSuccess: () => void;
}

const todayStr = () => new Date().toISOString().split("T")[0];

const emptyForm = (): Expense => ({
  description: "",
  payment_method: "Cash",
  reference_no: "",
  amount: 0,
  collected_date: todayStr(),
});

const ExpenseModal: React.FC<ExpenseModalProps> = ({
  show,
  onClose,
  mode,
  expenseData,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<Expense>(emptyForm());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show) {
      setFormData(emptyForm());
      return;
    }
    if (mode === "edit" && expenseData) {
      setFormData({
        description: expenseData.description || "",
        payment_method: expenseData.payment_method || "Cash",
        reference_no: expenseData.reference_no || "",
        amount: Number(expenseData.amount) || 0,
        collected_date: expenseData.collected_date
          ? String(expenseData.collected_date).split("T")[0]
          : todayStr(),
      });
    } else {
      setFormData(emptyForm());
    }
  }, [show, mode, expenseData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "amount" ? Number(value) : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.description || !formData.description.trim()) {
      toastr.error("Description is required");
      return;
    }
    if (!formData.amount || Number(formData.amount) <= 0) {
      toastr.error("Amount must be greater than 0");
      return;
    }

    // ── Payload: field names must exactly match what the backend destructures ──
    const payload = {
      description:      formData.description.trim(),
      payment_method:   formData.payment_method || "Cash",
      reference_no:     formData.reference_no || "",
      amount:           Number(formData.amount),
      collected_date:   formData.collected_date || todayStr(),
    };

    console.log("Submitting expense payload:", payload); // debug — remove after confirming

    setLoading(true);
    try {
      if (mode === "add") {
        await API.post("/finance/addCustomerPayment", payload);
        toastr.success("New Expense Details has been Added!");
      } else {
        if (!expenseData?.id) {
          toastr.error("Expense ID is missing");
          return;
        }
        await API.put(`/finance/updateCustomerPayment/${expenseData.id}`, payload);
        toastr.success("Expense has been Updated!");
      }
      onSuccess();
    } catch (error: any) {
      console.error("Submit error:", error);
      toastr.error(
        error?.response?.data?.message || `Failed to ${mode} expense`
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      key={`${mode}-${expenseData?.id || "new"}`}
      show={show}
      onHide={onClose}
      centered
      backdrop="static"
    >
      <Modal.Header closeButton className="bg-dark text-white border-secondary">
        <Modal.Title>
          {mode === "add" ? "Add Expense Details" : "Update Expense Details"}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body className="bg-dark text-white">

          {/* Date */}
          <Form.Group className="mb-3">
            <Form.Label>Date</Form.Label>
            <Form.Control
              type="date"
              name="collected_date"
              value={formData.collected_date || ""}
              onChange={handleChange}
              disabled={loading}
              className="bg-dark text-white border-secondary"
            />
          </Form.Group>

          {/* Description — name="description" must match payload key */}
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              type="text"
              name="description"
              value={formData.description}
              onChange={handleChange}
              maxLength={200}
              disabled={loading}
              className="bg-dark text-white border-secondary"
            />
          </Form.Group>

          {/* Payment Method */}
          <Form.Group className="mb-3">
            <Form.Label>Payment Method</Form.Label>
            <select
              name="payment_method"
              value={formData.payment_method}
              onChange={handleChange}
              disabled={loading}
              className="form-control bg-dark text-white border-secondary"
            >
              <option value="Cash">Cash</option>
              <option value="Credit Card">Credit Card</option>
              <option value="Debit Card">Debit Card</option>
              <option value="Online Payment">Online Payment</option>
              <option value="Cheque">Cheque</option>
              <option value="Demand Draft">Demand Draft</option>
            </select>
          </Form.Group>

          {/* Reference No. */}
          <Form.Group className="mb-3">
            <Form.Label>Reference No.</Form.Label>
            <Form.Control
              type="text"
              name="reference_no"
              value={formData.reference_no}
              onChange={handleChange}
              maxLength={100}
              disabled={loading}
              className="bg-dark text-white border-secondary"
            />
          </Form.Group>

          {/* Amount */}
          <Form.Group className="mb-3">
            <Form.Label>Amount</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              name="amount"
              value={formData.amount || ""}
              onChange={handleChange}
              min={0.01}
              disabled={loading}
              className="bg-dark text-white border-secondary"
            />
          </Form.Group>

        </Modal.Body>

        <Modal.Footer className="bg-dark border-secondary">
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ExpenseModal;